import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  MessageSquare, Heart, Pin, Plus, Send, User, 
  Clock, AlertTriangle, Lock, Trash2, ChevronDown, ChevronUp, Reply, Bell,
  Edit, Hash
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const forumRules = [
  "Be respectful and kind to all members",
  "Use neurodiversity-affirming language",
  "No spam, self-promotion, or off-topic content",
  "Protect privacy: do not share personal information about others",
  "Report any inappropriate content to moderators",
  "No discussion of harmful practices or organisations",
  "Support each other, we are all in this together"
];

// Extract hashtags from content
const extractHashtags = (text) => {
  const regex = /#(\w+)/g;
  const matches = text.match(regex);
  return matches ? matches.map(tag => tag.toLowerCase()) : [];
};

// Render content with clickable hashtags
const renderContentWithHashtags = (content, onHashtagClick) => {
  const parts = content.split(/(#\w+)/g);
  return parts.map((part, index) => {
    if (part.startsWith('#')) {
      return (
        <button
          key={index}
          onClick={() => onHashtagClick(part.toLowerCase())}
          className="text-primary hover:underline font-medium"
        >
          {part}
        </button>
      );
    }
    return part;
  });
};

// Truncate long content - lower threshold for better UX
const MAX_CONTENT_LENGTH = 300;

export default function Community() {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', anonymous: false });
  const [submitting, setSubmitting] = useState(false);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [expandedContent, setExpandedContent] = useState({});
  const [newComments, setNewComments] = useState({});
  const [postComments, setPostComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [hasNewPosts, setHasNewPosts] = useState(false);
  const [lastChecked, setLastChecked] = useState(() => {
    return localStorage.getItem('community_last_checked') || new Date().toISOString();
  });
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState({ title: '', content: '' });
  const [activeHashtag, setActiveHashtag] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/forum/posts`);
      setPosts(response.data);
      
      // Check for new posts since last visit
      const newPostsExist = response.data.some(post => 
        new Date(post.created_at) > new Date(lastChecked)
      );
      setHasNewPosts(newPostsExist);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  }, [lastChecked]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const markAsRead = () => {
    const now = new Date().toISOString();
    localStorage.setItem('community_last_checked', now);
    setLastChecked(now);
    setHasNewPosts(false);
  };

  const fetchComments = async (postId) => {
    if (postComments[postId]) return; // Already loaded
    
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const response = await axios.get(`${API}/forum/posts/${postId}`);
      setPostComments(prev => ({ ...prev, [postId]: response.data.comments || [] }));
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const togglePostExpanded = (postId) => {
    const newExpanded = !expandedPosts[postId];
    setExpandedPosts(prev => ({ ...prev, [postId]: newExpanded }));
    if (newExpanded) {
      fetchComments(postId);
    }
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!newPost.title.trim() || !newPost.content.trim()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/forum/posts`, newPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post created successfully!');
      setNewPost({ title: '', content: '', anonymous: false });
      setNewPostOpen(false);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitComment = async (postId) => {
    const commentContent = newComments[postId];
    if (!commentContent?.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/forum/posts/${postId}/comments`, 
        { content: commentContent, anonymous: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comment added!');
      setNewComments(prev => ({ ...prev, [postId]: '' }));
      // Refresh comments
      const response = await axios.get(`${API}/forum/posts/${postId}`);
      setPostComments(prev => ({ ...prev, [postId]: response.data.comments || [] }));
      // Update comments count in posts
      setPosts(prev => prev.map(p => 
        p.id === postId ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p
      ));
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleHeartPost = async (postId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to heart posts');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/forum/posts/${postId}/heart`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, hearts: response.data.hearts } : post
      ));
    } catch (error) {
      toast.error('Failed to heart post');
    }
  };

  const handleHeartComment = async (commentId, postId) => {
    if (!isAuthenticated) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/forum/comments/${commentId}/heart`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPostComments(prev => ({
        ...prev,
        [postId]: prev[postId].map(c => 
          c.id === commentId ? { ...c, hearts: response.data.hearts } : c
        )
      }));
    } catch (error) {
      console.error('Failed to heart comment');
    }
  };

  const handlePinPost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/forum/posts/${postId}/pin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPosts();
    } catch (error) {
      toast.error('Failed to pin post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/forum/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const handleEditPost = async () => {
    if (!editingPost) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/forum/posts/${editingPost}`, editContent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post updated');
      setEditingPost(null);
      setEditContent({ title: '', content: '' });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    }
  };

  const openEditDialog = (post) => {
    setEditingPost(post.id);
    setEditContent({ title: post.title, content: post.content });
  };

  const handleHashtagClick = (tag) => {
    if (activeHashtag === tag) {
      setActiveHashtag(null); // Clear filter
    } else {
      setActiveHashtag(tag);
    }
  };

  // Filter posts by hashtag
  const filteredPosts = activeHashtag 
    ? posts.filter(post => {
        const tags = extractHashtags(post.content + ' ' + post.title);
        return tags.includes(activeHashtag);
      })
    : posts;

  // Check if user can edit/delete a post
  const canEditPost = (post) => {
    if (!user) return false;
    return post.user_id === user.id;
  };

  const canDeletePost = (post) => {
    if (!user) return false;
    return isAdmin || post.user_id === user.id;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-12">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Badge>Community Forum</Badge>
              {hasNewPosts && (
                <Badge className="bg-primary text-white animate-pulse">
                  <Bell className="h-3 w-3 mr-1" />
                  New Posts!
                </Badge>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Connect & Share</h1>
            <p className="text-muted-foreground text-lg">
              A safe space to connect with other families, share experiences, and support each other. 
              Anonymous posting is available.
            </p>
            {hasNewPosts && (
              <Button variant="outline" size="sm" className="mt-4" onClick={markAsRead}>
                Mark all as read
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container-main py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Create Post */}
            {isAuthenticated ? (
              <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full btn-primary" size="lg" data-testid="create-post-btn">
                    <Plus className="h-5 w-5 mr-2" />
                    Start a Discussion
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Post</DialogTitle>
                    <DialogDescription>
                      Share your thoughts, questions, or experiences with the community.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmitPost} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        value={newPost.title}
                        onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                        placeholder="What's on your mind?"
                        required
                        data-testid="post-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="content">Content</Label>
                      <Textarea
                        id="content"
                        value={newPost.content}
                        onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                        placeholder="Share your thoughts..."
                        rows={5}
                        required
                        data-testid="post-content-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="anonymous"
                        checked={newPost.anonymous}
                        onCheckedChange={(checked) => setNewPost({ ...newPost, anonymous: checked })}
                        data-testid="post-anonymous-checkbox"
                      />
                      <Label htmlFor="anonymous" className="cursor-pointer">
                        Post anonymously
                      </Label>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={submitting} className="btn-primary" data-testid="submit-post-btn">
                        {submitting ? 'Posting...' : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Post
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <Card className="bg-muted/50">
                <CardContent className="p-6 text-center">
                  <Lock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground mb-4">
                    Sign in to join the discussion and connect with the community.
                  </p>
                  <Link to="/login">
                    <Button>Sign In to Post</Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Hashtag Filter */}
            {activeHashtag && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4 text-primary" />
                    <span>Showing posts tagged with <strong>{activeHashtag}</strong></span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setActiveHashtag(null)}>
                    Clear filter
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Posts List */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-6 bg-muted rounded w-2/3 mb-4"></div>
                      <div className="h-4 bg-muted rounded w-full mb-2"></div>
                      <div className="h-4 bg-muted rounded w-1/2"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredPosts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    {activeHashtag ? `No posts with ${activeHashtag}` : 'No Posts Yet'}
                  </h3>
                  <p className="text-muted-foreground">
                    {activeHashtag ? 'Try a different hashtag or clear the filter.' : 'Be the first to start a discussion!'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredPosts.map((post) => {
                  const isNew = new Date(post.created_at) > new Date(lastChecked);
                  const isLongContent = post.content.length > MAX_CONTENT_LENGTH;
                  const showFullContent = expandedContent[post.id] || !isLongContent;
                  
                  return (
                    <Card 
                      key={post.id} 
                      className={`card-base ${post.pinned ? 'border-primary/50 bg-primary/5' : ''} ${isNew ? 'ring-2 ring-primary/30' : ''}`}
                      data-testid={`post-${post.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              {post.pinned && (
                                <Badge className="bg-primary/20 text-primary">
                                  <Pin className="h-3 w-3 mr-1" />
                                  Pinned
                                </Badge>
                              )}
                              {isNew && (
                                <Badge className="bg-secondary/20 text-secondary">New</Badge>
                              )}
                              <h3 className="text-lg font-semibold">
                                {renderContentWithHashtags(post.title, handleHashtagClick)}
                              </h3>
                            </div>
                            
                            {/* Content with expand/collapse for long posts */}
                            <div className="text-muted-foreground mb-4">
                              <p className="whitespace-pre-wrap">
                                {showFullContent 
                                  ? renderContentWithHashtags(post.content, handleHashtagClick)
                                  : renderContentWithHashtags(post.content.slice(0, MAX_CONTENT_LENGTH) + '...', handleHashtagClick)
                                }
                              </p>
                              {isLongContent && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="mt-3"
                                  onClick={() => setExpandedContent(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                                >
                                  <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${showFullContent ? 'rotate-180' : ''}`} />
                                  {showFullContent ? 'Show less' : 'Read more'}
                                </Button>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                {post.anonymous ? (
                                  <>
                                    <User className="h-4 w-4" />
                                    Anonymous
                                  </>
                                ) : (
                                  <>
                                    <User className="h-4 w-4" />
                                    {post.author_name}
                                  </>
                                )}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleHeartPost(post.id)}
                              className="text-muted-foreground hover:text-pink-500"
                              data-testid={`heart-post-${post.id}`}
                            >
                              <Heart className="h-4 w-4 mr-1" />
                              {post.hearts}
                            </Button>
                            
                            {/* Edit button - only for post owner */}
                            {canEditPost(post) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(post)}
                                className="text-muted-foreground hover:text-primary h-8 w-8"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {/* Pin button - only for admins */}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePinPost(post.id)}
                                className={`h-8 w-8 ${post.pinned ? 'text-primary' : 'text-muted-foreground'}`}
                              >
                                <Pin className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {/* Delete button - for owner or admin */}
                            {canDeletePost(post) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeletePost(post.id)}
                                className="text-destructive h-8 w-8"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Comments Section */}
                        <Collapsible 
                          open={expandedPosts[post.id]} 
                          onOpenChange={() => togglePostExpanded(post.id)}
                          className="mt-4"
                        >
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="w-full justify-between">
                              <span className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4" />
                                {post.comments_count} {post.comments_count === 1 ? 'comment' : 'comments'}
                              </span>
                              {expandedPosts[post.id] ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-4 space-y-4">
                            {/* Comments List */}
                            {loadingComments[post.id] ? (
                              <div className="text-center py-4 text-muted-foreground">
                                Loading comments...
                              </div>
                            ) : (
                              <div className="space-y-3 pl-4 border-l-2 border-muted">
                                {(postComments[post.id] || []).map((comment) => (
                                  <div key={comment.id} className="bg-muted/30 rounded-lg p-3">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 text-sm mb-1">
                                          <span className="font-medium">
                                            {comment.anonymous ? 'Anonymous' : comment.author_name}
                                          </span>
                                          <span className="text-muted-foreground text-xs">
                                            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                          </span>
                                        </div>
                                        <p className="text-sm">{comment.content}</p>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleHeartComment(comment.id, post.id)}
                                        className="text-muted-foreground hover:text-pink-500 h-8 px-2"
                                      >
                                        <Heart className="h-3 w-3 mr-1" />
                                        {comment.hearts || 0}
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Add Comment */}
                            {isAuthenticated && (
                              <div className="flex gap-2 pt-2">
                                <Input
                                  value={newComments[post.id] || ''}
                                  onChange={(e) => setNewComments(prev => ({ 
                                    ...prev, 
                                    [post.id]: e.target.value 
                                  }))}
                                  placeholder="Write a comment..."
                                  className="flex-1"
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSubmitComment(post.id);
                                    }
                                  }}
                                />
                                <Button 
                                  size="icon" 
                                  onClick={() => handleSubmitComment(post.id)}
                                  disabled={!newComments[post.id]?.trim()}
                                >
                                  <Reply className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </CollapsibleContent>
                        </Collapsible>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* Hashtag Filter Sidebar */}
            <Card data-testid="hashtag-sidebar">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Hash className="h-5 w-5 text-primary" />
                  Topics & Hashtags
                </CardTitle>
                <CardDescription>Filter posts by topic</CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  // Extract all hashtags from all posts
                  const allHashtags = posts.flatMap(post => extractHashtags(post.content + ' ' + post.title));
                  const hashtagCounts = allHashtags.reduce((acc, tag) => {
                    acc[tag] = (acc[tag] || 0) + 1;
                    return acc;
                  }, {});
                  const sortedHashtags = Object.entries(hashtagCounts)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 10);
                  
                  if (sortedHashtags.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground">
                        No hashtags yet. Use #hashtags in your posts to create topics!
                      </p>
                    );
                  }
                  
                  return (
                    <div className="flex flex-wrap gap-2">
                      {sortedHashtags.map(([tag, count]) => (
                        <Button
                          key={tag}
                          variant={activeHashtag === tag ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleHashtagClick(tag)}
                          className={`text-xs ${activeHashtag === tag ? 'btn-primary' : ''}`}
                          data-testid={`hashtag-${tag.replace('#', '')}`}
                        >
                          {tag}
                          <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                            {count}
                          </Badge>
                        </Button>
                      ))}
                      {activeHashtag && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setActiveHashtag(null)}
                          className="text-xs text-muted-foreground"
                        >
                          Clear filter
                        </Button>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Community Rules */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-accent" />
                  Community Guidelines
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {forumRules.map((rule, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary font-bold">{i + 1}.</span>
                      <span className="text-muted-foreground">{rule}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Support Groups */}
            <Card>
              <CardHeader>
                <CardTitle>Support Groups</CardTitle>
                <CardDescription>Connect with other families</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <a 
                  href="https://www.facebook.com/groups/416389465908329" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full justify-start">
                    Caymums & Dads Facebook Group
                  </Button>
                </a>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>

      {/* Edit Post Dialog */}
      <Dialog open={!!editingPost} onOpenChange={(open) => !open && setEditingPost(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
            <DialogDescription>Make changes to your post.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={editContent.title}
                onChange={(e) => setEditContent({ ...editContent, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-content">Content</Label>
              <Textarea
                id="edit-content"
                value={editContent.content}
                onChange={(e) => setEditContent({ ...editContent, content: e.target.value })}
                rows={5}
              />
              <p className="text-xs text-muted-foreground">
                Tip: Use #hashtags to categorize your post and help others find related discussions.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPost(null)}>Cancel</Button>
            <Button onClick={handleEditPost} className="btn-primary">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
